import React, { useState, useEffect } from "react";
import {
  savePaymentGatewayConfig, getPaymentGatewayConfigs, getPaymentGatewayConfigById,
  updatePaymentGatewayConfig, deletePaymentGatewayConfig, getLookupByGroup,
  togglePaymentGatewayStatusApi
} from "../../services/adminApi";
import "./PaymentGatewayConfig.css";
import Swal from "sweetalert2";
import GatewayTable from "./payment/components/GatewayTable";
import GatewayForm from "./payment/components/GatewayForm";

const PaymentGatewayConfig = () => {
  const [loading, setLoading] = useState(false);
  const [gatewayList, setGatewayList] = useState([]);
  const [isPaymentEnabled, setIsPaymentEnabled] = useState(true);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [lookups, setLookups] = useState({ providers: [], modes: [] });
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    provider: "",
    active_mode: "",
    demo_api_key: "",
    demo_secret_key: "",
    live_api_key: "",
    live_secret_key: ""
  });

  const fetchLookups = async () => {
    try {
      const [res1, res2] = await Promise.all([
        getLookupByGroup("paymentGateway"),
        getLookupByGroup("paymentMode")
      ]);

      const rawProviders = res1?.data?.data || res1?.data || [];
      const rawModes = res2?.data?.data || res2?.data || [];

      const providersList = Array.isArray(rawProviders)
        ? rawProviders.map(item => ({
            key: item.lookupValue || item.key || item.value,
            value: item.lookupLabel || item.value || item.label || item.lookupValue
          }))
        : [];

      const modesList = Array.isArray(rawModes)
        ? rawModes.map(item => ({
            key: item.lookupValue || item.key || item.value,
            value: item.lookupLabel || item.value || item.label || item.lookupValue
          }))
        : [];

      setLookups({
        providers: providersList,
        modes: modesList
      });

      if (providersList.length > 0) {
        setFormData(prev => ({
          ...prev,
          provider: prev.provider || providersList[0].key,
          active_mode: prev.active_mode || (modesList[0]?.key || "")
        }));
      }
    } catch (err) {
      console.log("Error fetching lookups", err);
    }
  };

  const loadGatewayList = async () => {
    try {
      const res = await getPaymentGatewayConfigs();
      if (res.data.success) {
        const list = res.data.data || [];
        setGatewayList(list);
        if (list.length > 0) {
          const anyActive = list.some(g => g.status === true || g.status === 1);
          setIsPaymentEnabled(anyActive);
        }
      }
    } catch (err) { console.log(err); }
  };

  const handleTogglePaymentStatus = async () => {
    const nextStatus = !isPaymentEnabled;
    const result = await Swal.fire({
      title: nextStatus ? "Enable Online Payments?" : "Disable Online Payments?",
      html: nextStatus
        ? `<p class="text-light">Enabling payments will require candidates to pay fees via the online Razorpay gateway.</p>`
        : `<div style="text-align: left; font-size: 0.95rem;">
             <p class="text-warning mb-2"><b>Notice:</b> When online payments are disabled:</p>
             <ul class="text-light ps-3 mb-0">
               <li>Razorpay payment gateway will NOT be opened for candidates.</li>
               <li>Candidates can apply for free using available 100% OFF launch coupons.</li>
             </ul>
           </div>`,
      icon: nextStatus ? "question" : "warning",
      showCancelButton: true,
      confirmButtonText: nextStatus ? "Yes, Enable Payments" : "Yes, Disable Payments",
      confirmButtonColor: nextStatus ? "#10B981" : "#DC2626",
      cancelButtonText: "Cancel"
    });

    if (!result.isConfirmed) return;

    try {
      setToggleLoading(true);
      const res = await togglePaymentGatewayStatusApi({ isEnabled: nextStatus });
      if (res.data.success) {
        setIsPaymentEnabled(res.data.isPaymentEnabled);
        Swal.fire({
          icon: "success",
          title: "Updated!",
          text: res.data.message || `Payments are now ${res.data.isPaymentEnabled ? "ENABLED" : "DISABLED"}.`
        });
        loadGatewayList();
      }
    } catch (err) {
      console.error("Toggle error:", err);
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: err?.response?.data?.message || "Failed to toggle payment status"
      });
    } finally {
      setToggleLoading(false);
    }
  };

  useEffect(() => {
    loadGatewayList();
    fetchLookups();
  }, []);

  const handleEdit = async (id) => {
    try {
      const res = await getPaymentGatewayConfigById(id);
      if (res.data.success) {
        const config = res.data.data;
        setFormData({
          provider: config.provider || "",
          active_mode: config.active_mode || "",
          demo_api_key: config.demo_api_key || "",
          demo_secret_key: config.demo_secret_key || "",
          live_api_key: config.live_api_key || "",
          live_secret_key: config.live_secret_key || ""
        });
        setEditId(id);
        setShowForm(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (err) { console.log(err); }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc0000",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Delete It!"
    });

    if (!result.isConfirmed) return;

    try {
      const response = await deletePaymentGatewayConfig(id);

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Deleted",
          text: "Configuration Deleted Successfully"
        });

        loadGatewayList();
      }
    } catch (error) {
      console.error(error);

      Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text: error?.response?.data?.message || "Something went wrong"
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setErrors({}); 
    
    if (name === "provider") {
      setFormData(prev => ({ 
        ...prev, 
        provider: value, 
        demo_api_key: "", demo_secret_key: "", 
        live_api_key: "", live_secret_key: "" 
      }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  
  const validateGatewayKeys = () => {
    let newErrors = {};
    if (formData.provider === "dummy" || formData.provider === "dummyGateway") {
      setErrors({});
      return true;
    }
    const isDemo = formData.active_mode === "demo";
    const api = isDemo ? formData.demo_api_key : formData.live_api_key;
    const secret = isDemo ? formData.demo_secret_key : formData.live_secret_key;

    if (!api?.trim()) newErrors.apiKey = `${isDemo ? "Demo" : "Live"} API Key is required`;
    if (!secret?.trim()) newErrors.secretKey = `${isDemo ? "Demo" : "Live"} Secret Key is required`;
    
    if (formData.provider === "razorpay" && api?.trim() && !/^rzp_(test|live)_[a-zA-Z0-9]+$/.test(api)) {
      newErrors.apiKey = "Invalid Razorpay API Key";
    }
    if (formData.provider === "razorpay" && secret?.trim() && secret.length < 10) {
      newErrors.secretKey = "Invalid Razorpay Secret Key";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateGatewayKeys()) return;

    try {
      setLoading(true);

      const response = editId
        ? await updatePaymentGatewayConfig(editId, formData)
        : await savePaymentGatewayConfig(formData);

      if (response.data.success) {
        await Swal.fire({
          icon: "success",
          title: editId ? "Updated Successfully" : "Saved Successfully",
          text: editId
            ? "Payment Gateway Configuration Updated Successfully"
            : "Payment Gateway Configuration Saved Successfully",
          confirmButtonColor: "#198754"
        });

        loadGatewayList();

        setFormData({
          provider: lookups.providers[0]?.key || "",
          active_mode: lookups.modes[0]?.key || "",
          demo_api_key: "",
          demo_secret_key: "",
          live_api_key: "",
          live_secret_key: ""
        });

        setShowForm(false);
        setEditId(null);
      }
    } catch (error) {
      console.error(error);

      Swal.fire({
        icon: "error",
        title: "Operation Failed",
        text:
          error?.response?.data?.message ||
          "Failed To Save Configuration",
        confirmButtonColor: "#dc3545"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="payment-gateway-admin-page">
      <div className="payment-gateway-card">
        <div className="payment-gateway-header d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div>
            <h4 className="payment-gateway-title mb-1">Payment Gateway Configuration</h4>
            <div className="d-flex align-items-center gap-2 mt-2">
              <span className="badge px-3 py-2" style={{ 
                backgroundColor: isPaymentEnabled ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                color: isPaymentEnabled ? '#10B981' : '#EF4444',
                border: `1px solid ${isPaymentEnabled ? '#10B981' : '#EF4444'}`,
                fontSize: '0.85rem'
              }}>
                ● Online Payments: <strong>{isPaymentEnabled ? 'ENABLED' : 'DISABLED'}</strong>
              </span>
              <button 
                type="button" 
                className={`btn btn-sm ${isPaymentEnabled ? 'btn-outline-danger' : 'btn-outline-success'} fw-bold px-3`}
                onClick={handleTogglePaymentStatus}
                disabled={toggleLoading}
              >
                {toggleLoading ? 'Updating...' : isPaymentEnabled ? 'Turn Off / Disable Payments' : 'Turn On / Enable Payments'}
              </button>
            </div>
          </div>
          <button className="add-new-btn" onClick={() => {
            setEditId(null);
            setFormData({
              provider: lookups.providers[0]?.key || "",
              active_mode: lookups.modes[0]?.key || "",
              demo_api_key: "",
              demo_secret_key: "",
              live_api_key: "",
              live_secret_key: ""
            });
            setErrors({});
            setShowForm(true);
          }}>+ Add New</button>
        </div>

        {!showForm && (
          <GatewayTable
            gatewayList={gatewayList}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
          />
        )}

        {showForm && (
          <GatewayForm
            formData={formData}
            lookups={lookups}
            errors={errors}
            loading={loading}
            editId={editId}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            onClose={() => setShowForm(false)}
          />
        )}
      </div>
    </div>
  );
};

export default PaymentGatewayConfig;